import { useState, useEffect } from 'react';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import ListLoadError from '../../components/admin/ListLoadError';
import styles from './PromoCodesPage.module.css';

const EMPTY_FORM = {
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: '',
  freeMonths: '',
  minOrderAmount: '',
  maxDiscountAmount: '',
  maxUses: '',
  expiresAt: '',
  isActive: true,
  isReferralPromo: false,
  promoType: 'code',
  applicableMonths: [],
  subscriptionDuration: null,
  applyToAll: false,
  waiverTargets: []
};

const DURATION_OPTIONS = [1, 3, 6, 12];

const EXAMPLE_BASE_PRICE = 10000; // $100.00 in cents for preview

const inferDuration = (applicableMonths) => {
  if (!applicableMonths || typeof applicableMonths !== 'object') return null;
  const keys = Object.keys(applicableMonths).map(Number).filter(Boolean);
  if (keys.length === 0) return null;
  const maxMonth = Math.max(...keys);
  return DURATION_OPTIONS.find((d) => d >= maxMonth) ?? maxMonth;
};

const buildMonthsArray = (duration, existingMonths = {}) =>
  Array.from({ length: duration }, (_, i) => ({
    month: i + 1,
    discount: existingMonths[String(i + 1)] ?? ''
  }));

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().split('T')[0];
};

const PromoCodesPage = () => {
  const { toast } = useToast();

  // Card #6238 c4. Keys mirror what the admin router enforces
  // (`config/adminPermissionCodes.js`): POST -> `.create`, PATCH -> `.edit`,
  // DELETE -> `.delete` (promo_codes defines all three, so there is no `.edit`
  // fallback here and each control must be gated on its OWN key).
  const { hasPermission } = useAuth();
  const canCreatePromo = hasPermission('promo_codes.create');
  const canEditPromo = hasPermission('promo_codes.edit');
  const canDeletePromo = hasPermission('promo_codes.delete');
  const [promoCodes, setPromoCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (page = 1) => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);

      const [promoRes, statsRes] = await Promise.all([
        api.get(`/admin/promo-codes?${params}`),
        api.get('/admin/promo-codes/stats')
      ]);

      setPromoCodes(promoRes.promoCodes || []);
      setPagination(promoRes.pagination || null);
      setStats(statsRes.stats || null);
    } catch (error) {
      setLoadError(true);
      setPromoCodes([]);
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadData(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const openModal = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      const duration = inferDuration(promo.applicableMonths);
      const monthsObj = promo.applicableMonths || {};
      setFormData({
        code: promo.code || '',
        description: promo.description || '',
        discountType: promo.discountType || 'percentage',
        discountValue: promo.discountValue || '',
        freeMonths: promo.freeMonths || '',
        minOrderAmount: promo.minOrderAmount || '',
        maxDiscountAmount: promo.maxDiscountAmount || '',
        maxUses: promo.maxUses || '',
        expiresAt: formatDateForInput(promo.expiresAt),
        isActive: promo.isActive !== false,
        isReferralPromo: promo.isReferralPromo === true,
        promoType: promo.promoType || 'code',
        subscriptionDuration: duration,
        applyToAll: false,
        applicableMonths: duration ? buildMonthsArray(duration, monthsObj) : [],
        waiverTargets: promo.waiverTargets || []
      });
    } else {
      setEditingPromo(null);
      setFormData(EMPTY_FORM);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingPromo(null);
    setFormData(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isSystemPromo = formData.promoType === 'system_product';

    // Only require code for user code promos
    if (!isSystemPromo && !formData.code.trim()) {
      toast.error('Promo code is required');
      return;
    }

    // Validate applicable_months for system promos
    if (isSystemPromo && !formData.subscriptionDuration) {
      toast.error('Select a subscription duration');
      return;
    }

    if (isSystemPromo) {
      const hasAnyDiscount = formData.applicableMonths.some((m) => m.discount > 0);
      if (!hasAnyDiscount) {
        toast.error('Set a discount for at least one month');
        return;
      }
      const invalidMonth = formData.applicableMonths.find(
        (m) => m.discount !== '' && m.discount !== 0 && (m.discount < 0 || m.discount > 100)
      );
      if (invalidMonth) {
        toast.error(`Month ${invalidMonth.month}: discount must be 0-100%`);
        return;
      }
    }

    // Validate waiver targets
    if (formData.discountType === 'waiver' && formData.waiverTargets.length === 0) {
      toast.error('Select at least one waiver target');
      return;
    }

    // Validate based on discount type
    if (formData.discountType === 'free_months') {
      const months = parseInt(formData.freeMonths);
      if (!months || months < 1 || months > 12) {
        toast.error('Free months must be between 1 and 12');
        return;
      }
    } else if (formData.discountType !== 'waiver') {
      if (!formData.discountValue || parseInt(formData.discountValue) <= 0) {
        toast.error('Discount value must be greater than 0');
        return;
      }
    }

    setSaving(true);
    try {
      // Convert applicableMonths array to object for API — empty months default to 0
      const applicableMonthsObj = isSystemPromo
        ? formData.applicableMonths.reduce((acc, { month, discount }) => ({ ...acc, [String(month)]: discount || 0 }), {})
        : null;

      const payload = {
        ...formData,
        promoType: formData.promoType,
        applicableMonths: applicableMonthsObj,
        waiverTargets: formData.discountType === 'waiver' ? formData.waiverTargets : null,
        // System promos don't send a code — backend auto-generates
        code: isSystemPromo ? undefined : formData.code,
        discountValue: formData.discountType === 'free_months' || formData.discountType === 'waiver' ? 0 : parseInt(formData.discountValue),
        freeMonths: formData.discountType === 'free_months' ? parseInt(formData.freeMonths) : null,
        minOrderAmount: formData.minOrderAmount ? parseInt(formData.minOrderAmount) : 0,
        maxDiscountAmount: formData.maxDiscountAmount ? parseInt(formData.maxDiscountAmount) : null,
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
        expiresAt: formData.expiresAt || null
      };

      if (editingPromo) {
        await api.patch(`/admin/promo-codes/${editingPromo.id}`, payload);
        toast.success('Promo code updated');
      } else {
        await api.post('/admin/promo-codes', payload);
        toast.success('Promo code created');
      }
      closeModal();
      loadData(1);
    } catch (error) {
      toast.error(error.message || 'Failed to save promo code');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promo) => {
    if (!window.confirm(`Delete promo code "${promo.code}"?`)) return;

    try {
      await api.delete(`/admin/promo-codes/${promo.id}`);
      setPromoCodes((prev) => prev.filter((p) => p.id !== promo.id));
      toast.success('Promo code deleted');
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const handleToggleStatus = async (promo) => {
    try {
      const response = await api.patch(`/admin/promo-codes/${promo.id}/status`);
      setPromoCodes((prev) =>
        prev.map((p) => (p.id === promo.id ? response.promoCode : p))
      );
      toast.success(response.message);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const isExpired = (promo) =>
    promo.expiresAt && new Date(promo.expiresAt) < new Date();

  const isMaxedOut = (promo) =>
    promo.maxUses && promo.currentUses >= promo.maxUses;

  const getStatusLabel = (promo) => {
    if (!promo.isActive) return { label: 'Inactive', className: styles.inactive };
    if (isExpired(promo)) return { label: 'Expired', className: styles.expired };
    if (isMaxedOut(promo)) return { label: 'Maxed Out', className: styles.maxedOut };
    return { label: 'Active', className: styles.active };
  };

  const formatDiscount = (promo) => {
    if (promo.discountType === 'waiver') {
      const targets = promo.waiverTargets || [];
      const labels = { consultation_fee: 'Consult', shipping_cost: 'Shipping', medication_month: 'Rx Month', membership_month: 'Membership Mo' };
      return targets.map((t) => labels[t] || t).join(', ') || 'Waiver';
    }
    if (promo.discountType === 'free_months') {
      const months = promo.freeMonths || 1;
      return `${months} month${months > 1 ? 's' : ''} free`;
    }
    if (promo.discountType === 'percentage') {
      return `${promo.discountValue}%`;
    }
    return `$${(promo.discountValue / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <p>Loading promo codes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            Promo <span className={styles.accentText}>Codes</span>
          </h1>
          <p className={styles.subtitle}>
            Create and manage discount codes for customers
          </p>
        </div>
        {canCreatePromo && (
        <button className={styles.createButton} onClick={() => openModal()}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Create Code
        </button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{stats.total}</span>
            <span className={styles.statLabel}>Total Codes</span>
          </div>
          <div className={styles.statCard}>
            <span className={`${styles.statValue} ${styles.green}`}>{stats.active}</span>
            <span className={styles.statLabel}>Active</span>
          </div>
          <div className={styles.statCard}>
            <span className={`${styles.statValue} ${styles.amber}`}>{stats.expired}</span>
            <span className={styles.statLabel}>Expired</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{stats.totalUses}</span>
            <span className={styles.statLabel}>Total Uses</span>
          </div>
        </div>
      )}

      {/* Search */}
      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder="Search by code or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          className={styles.searchInput}
        />
        <button onClick={handleSearch} className={styles.searchButton}>
          Search
        </button>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        {loadError ? (
          <ListLoadError message="Could not load promo codes" onRetry={() => loadData(1)} />
        ) : promoCodes.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
            </svg>
            <h3>No promo codes yet</h3>
            <p>Create your first promo code to offer discounts to customers.</p>
            {canCreatePromo && (
            <button className={styles.createButton} onClick={() => openModal()}>
              Create Code
            </button>
            )}
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Description</th>
                <th>Discount</th>
                <th>Uses</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {promoCodes.map((promo) => {
                const status = getStatusLabel(promo);
                return (
                  <tr key={promo.id}>
                    <td>
                      <span className={styles.codeCell}>{promo.code}</span>
                      {promo.isReferralPromo && <span className={styles.referralBadge}>Referral</span>}
                    </td>
                    <td>
                      <span className={`${styles.typeBadge} ${promo.promoType === 'system_product' ? styles.typeBadgeSystem : styles.typeBadgeCode}`}>
                        {promo.promoType === 'system_product' ? 'System Product' : 'User Code'}
                      </span>
                    </td>
                    <td className={styles.descCell}>
                      {promo.description || <span className={styles.muted}>No description</span>}
                    </td>
                    <td>
                      <span className={styles.discountBadge}>
                        {formatDiscount(promo)}
                      </span>
                    </td>
                    <td>
                      {promo.currentUses || 0}
                      {promo.maxUses ? ` / ${promo.maxUses}` : ''}
                    </td>
                    <td>
                      {promo.expiresAt ? formatDate(promo.expiresAt) : <span className={styles.muted}>Never</span>}
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${status.className}`}>
                        {status.label}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        {canEditPromo && (
                        <button
                          className={styles.actionBtn}
                          onClick={() => openModal(promo)}
                          title="Edit"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        )}
                        {canEditPromo && (
                        <button
                          className={`${styles.actionBtn} ${promo.isActive ? styles.deactivateBtn : styles.activateBtn}`}
                          onClick={() => handleToggleStatus(promo)}
                          title={promo.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {promo.isActive ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </button>
                        )}
                        {canDeletePromo && (
                        <button
                          className={`${styles.actionBtn} ${styles.deleteBtn}`}
                          onClick={() => handleDelete(promo)}
                          title="Delete"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                          </svg>
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            disabled={pagination.page <= 1}
            onClick={() => loadData(pagination.page - 1)}
            className={styles.pageBtn}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => loadData(pagination.page + 1)}
            className={styles.pageBtn}
          >
            Next
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className={styles.overlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{editingPromo ? 'Edit Promo Code' : 'Create Promo Code'}</h2>
              <button className={styles.closeBtn} onClick={closeModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Promo Type Selector */}
              <div className={styles.promoTypeSelector}>
                <button
                  type="button"
                  className={`${styles.promoTypeBtn} ${formData.promoType === 'code' ? styles.promoTypeBtnActive : ''}`}
                  onClick={() => setFormData({ ...formData, promoType: 'code' })}
                >
                  User Code
                </button>
                <button
                  type="button"
                  className={`${styles.promoTypeBtn} ${formData.promoType === 'system_product' ? styles.promoTypeBtnActive : ''}`}
                  onClick={() => setFormData({ ...formData, promoType: 'system_product' })}
                >
                  System Product
                </button>
              </div>

              <div className={styles.formGrid}>
                {formData.promoType === 'code' && (
                  <div className={styles.formGroup}>
                    <label>Promo Code *</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., WELCOME20"
                      required
                    />
                  </div>
                )}

                {formData.promoType === 'system_product' && (
                  <div className={styles.formGroup}>
                    <label>Code</label>
                    <input
                      type="text"
                      value={editingPromo?.code || 'Auto-generated'}
                      disabled
                      style={{ opacity: 0.5, cursor: 'not-allowed' }}
                    />
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label>Discount Type</label>
                  <select
                    value={formData.discountType}
                    onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                    <option value="free_months">Free Month(s)</option>
                    <option value="waiver">Waiver</option>
                  </select>
                </div>
              </div>

              {formData.discountType === 'waiver' ? (
                <div className={styles.waiverSection}>
                  <label className={styles.waiverSectionLabel}>Waiver Targets *</label>
                  <p className={styles.waiverSectionHelper}>Select which fees to waive when this promo is applied</p>
                  <div className={styles.waiverGrid}>
                    {[
                      { value: 'consultation_fee', label: 'Consultation Fee' },
                      { value: 'shipping_cost', label: 'Shipping & Handling' },
                      { value: 'medication_month', label: 'One Month Medication' },
                      { value: 'membership_month', label: 'One Month Membership' }
                    ].map((target) => (
                      <label key={target.value} className={styles.waiverTargetLabel}>
                        <input
                          type="checkbox"
                          checked={formData.waiverTargets.includes(target.value)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...formData.waiverTargets, target.value]
                              : formData.waiverTargets.filter((t) => t !== target.value);
                            setFormData({ ...formData, waiverTargets: updated });
                          }}
                          className={styles.waiverCheckbox}
                        />
                        <span>{target.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className={styles.formGrid} style={{ marginTop: '1rem' }}>
                    <div className={styles.formGroup}>
                      <label>Max Uses</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.maxUses}
                        onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                        placeholder="Unlimited"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.formGrid}>
                  {formData.discountType === 'free_months' ? (
                    <div className={styles.formGroup}>
                      <label>Number of Free Months *</label>
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={formData.freeMonths}
                        onChange={(e) => setFormData({ ...formData, freeMonths: e.target.value })}
                        placeholder="e.g., 1"
                        required
                      />
                    </div>
                  ) : (
                    <div className={styles.formGroup}>
                      <label>
                        Discount Value * {formData.discountType === 'percentage' ? '(%)' : '(cents)'}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max={formData.discountType === 'percentage' ? 100 : undefined}
                        value={formData.discountValue}
                        onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                        placeholder={formData.discountType === 'percentage' ? 'e.g., 20' : 'e.g., 1000 (= $10.00)'}
                        required
                      />
                    </div>
                  )}

                  <div className={styles.formGroup}>
                    <label>Max Uses</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.maxUses}
                      onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                      placeholder="Unlimited"
                    />
                  </div>
                </div>
              )}

              <div className={styles.formGroupFull}>
                <label>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Friends & Family discount"
                />
              </div>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Min Order Amount (cents)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.minOrderAmount}
                    onChange={(e) => setFormData({ ...formData, minOrderAmount: e.target.value })}
                    placeholder="0 (no minimum)"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Max Discount Amount (cents)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.maxDiscountAmount}
                    onChange={(e) => setFormData({ ...formData, maxDiscountAmount: e.target.value })}
                    placeholder="No limit"
                  />
                </div>
              </div>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Expiration Date</label>
                  <input
                    type="date"
                    value={formData.expiresAt}
                    onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              {/* Duration & Month-by-Month Discounts (System Product only) */}
              {formData.promoType === 'system_product' && (
                <div className={styles.monthsSection}>
                  <label className={styles.monthsSectionLabel}>Subscription Duration</label>
                  <p className={styles.monthsSectionHelper}>
                    Choose how many months this promo covers, then set a discount for each month
                  </p>

                  {/* Duration selector */}
                  <div className={styles.durationSelector}>
                    {DURATION_OPTIONS.map((d) => (
                      <button
                        key={d}
                        type="button"
                        className={`${styles.durationBtn} ${formData.subscriptionDuration === d ? styles.durationBtnActive : ''}`}
                        onClick={() => {
                          const existing = formData.applicableMonths.reduce(
                            (acc, m) => ({ ...acc, [String(m.month)]: m.discount }), {}
                          );
                          setFormData({
                            ...formData,
                            subscriptionDuration: d,
                            applicableMonths: buildMonthsArray(d, existing),
                            applyToAll: false
                          });
                        }}
                      >
                        {d} {d === 1 ? 'month' : 'months'}
                      </button>
                    ))}
                  </div>

                  {/* Month-by-month discount inputs */}
                  {formData.subscriptionDuration && (
                    <>
                      {/* Apply to all toggle */}
                      <div className={styles.applyAllRow}>
                        <label className={styles.applyAllLabel}>
                          <input
                            type="checkbox"
                            checked={formData.applyToAll}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              if (checked) {
                                const firstDiscount = formData.applicableMonths[0]?.discount || '';
                                setFormData({
                                  ...formData,
                                  applyToAll: true,
                                  applicableMonths: formData.applicableMonths.map((m) => ({ ...m, discount: firstDiscount }))
                                });
                              } else {
                                setFormData({ ...formData, applyToAll: false });
                              }
                            }}
                            className={styles.applyAllCheckbox}
                          />
                          <span>Apply same discount to all months</span>
                        </label>
                      </div>

                      <div className={styles.monthInputsGrid}>
                        {formData.applicableMonths.map((entry, idx) => {
                          const discountVal = entry.discount === '' ? '' : Number(entry.discount);
                          const isInvalid = discountVal !== '' && (discountVal < 0 || discountVal > 100);
                          return (
                            <div key={entry.month} className={styles.monthInputCard}>
                              <span className={styles.monthInputLabel}>Month {entry.month}</span>
                              <div className={styles.monthInputWrapper}>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={entry.discount}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? '' : parseInt(e.target.value);
                                    if (formData.applyToAll) {
                                      setFormData({
                                        ...formData,
                                        applicableMonths: formData.applicableMonths.map((m) => ({ ...m, discount: val }))
                                      });
                                    } else {
                                      const updated = formData.applicableMonths.map((m, i) =>
                                        i === idx ? { ...m, discount: val } : m
                                      );
                                      setFormData({ ...formData, applicableMonths: updated });
                                    }
                                  }}
                                  placeholder="0"
                                  className={isInvalid ? styles.monthInputError : ''}
                                />
                                <span className={styles.monthInputSuffix}>%</span>
                              </div>
                              {isInvalid && (
                                <span className={styles.monthErrorText}>0-100 only</span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Live preview panel */}
                      <div className={styles.previewPanel}>
                        <span className={styles.previewTitle}>Preview (based on $100/mo)</span>
                        <div className={styles.previewRows}>
                          {formData.applicableMonths.map((entry) => {
                            const pct = entry.discount || 0;
                            const base = EXAMPLE_BASE_PRICE;
                            const discounted = Math.round(base * (1 - pct / 100));
                            return (
                              <div key={entry.month} className={styles.previewRow}>
                                <span className={styles.previewMonth}>Mo {entry.month}</span>
                                <span className={styles.previewCalc}>
                                  ${(base / 100).toFixed(2)} x {100 - pct}%
                                </span>
                                <span className={styles.previewPrice}>
                                  = ${(discounted / 100).toFixed(2)}
                                </span>
                              </div>
                            );
                          })}
                          <div className={`${styles.previewRow} ${styles.previewTotal}`}>
                            <span className={styles.previewMonth}>Total</span>
                            <span className={styles.previewCalc}>
                              Saved ${(formData.applicableMonths.reduce((sum, m) => sum + Math.round(EXAMPLE_BASE_PRICE * ((m.discount || 0) / 100)), 0) / 100).toFixed(2)}
                            </span>
                            <span className={styles.previewPrice}>
                              = ${(formData.applicableMonths.reduce((sum, m) => sum + Math.round(EXAMPLE_BASE_PRICE * (1 - (m.discount || 0) / 100)), 0) / 100).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Referral Promo Toggle */}
              <div className={styles.referralToggleRow}>
                <label className={styles.toggleLabel}>
                  <div className={styles.toggleWrapper}>
                    <input
                      type="checkbox"
                      checked={formData.isReferralPromo}
                      onChange={(e) => setFormData({ ...formData, isReferralPromo: e.target.checked })}
                      className={styles.toggleInput}
                    />
                    <span className={styles.toggleSwitch} />
                  </div>
                  <div className={styles.toggleText}>
                    <span className={styles.toggleTitle}>Referral Promo</span>
                    <span className={styles.toggleHelper}>When enabled, this promo code appears as a reward option in the Refer a Friend program</span>
                  </div>
                </label>
              </div>

              <div className={styles.modalFooter}>
                <button type="button" className={styles.cancelBtn} onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className={styles.submitBtn} disabled={saving}>
                  {saving ? 'Saving...' : editingPromo ? 'Update Code' : 'Create Code'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodesPage;
